// The `/auth` routes the console uses, and the shapes they answer with (#231).
//
// Written against `control_plane/routes/auth.py` (#160), which is on `main`;
// the paths and the field names below are read off it rather than guessed, and
// this file is the only place in the SPA that names one.
//
// **`POST /auth/mfa/complete` is what finishes a login, not `/auth/mfa/verify`.**
// #231's text names `/auth/mfa/verify`; the route of that name is MFA
// *enrolment* — it takes a `code` on an already-authenticated session and hands
// back recovery codes. The login challenge is `mfa_complete`, which takes the
// `mfa_token` that `POST /auth/login` answered with. The route is the contract.
//
// **Nothing here lists the contexts a user may select**, because no route does:
// `select_context` reads the membership under the context it is given
// (`auth/service.py::select_context`) and there is no `GET /auth/memberships`.
// So context selection asks for the id rather than offering a list, and says so
// on the screen. A list is a route this ticket does not add (#231, "not in
// scope"; the membership itself is the owner's #152 Q5 worker job).

import type { PartnerRef } from "./customers";
import type { Api } from "./http";

export const SESSION_PATH = "/auth/session";
export const CONTEXT_PATH = "/auth/session/context";
export const LOGIN_PATH = "/auth/login";
export const REGISTER_PATH = "/auth/register";
export const MFA_COMPLETE_PATH = "/auth/mfa/complete";
export const LOGOUT_PATH = "/auth/logout";
export const MEMBERSHIPS_PATH = "/auth/memberships";
// The doors a mail opens (#415): each spends the token its link carried.
export const PASSWORD_RESET_PATH = "/auth/password/reset";
export const PASSWORD_RESET_CONFIRM_PATH = "/auth/password/reset/confirm";
export const EMAIL_VERIFY_PATH = "/auth/email/verify";
export const EMAIL_VERIFY_CONFIRM_PATH = "/auth/email/verify/confirm";
export const MAGIC_LINK_CONFIRM_PATH = "/auth/magic-link/confirm";

/** `User.public()`: the profile the API shows, never the hash. */
export interface PublicUser {
  user_id: string;
  email: string;
  display_name: string | null;
  password_set: boolean;
  email_verified: boolean;
  superadmin: boolean;
  status: string;
  selected_workspace_id: string | null;
  selected_org_id: string | null;
  sso_linked: boolean;
}

/** `GET /auth/session`. `workspace_id` and `org_id` are the selected context. */
export interface SessionResponse {
  user: PublicUser;
  session: Record<string, unknown> | null;
  workspace_id: string | null;
  org_id: string | null;
  /** The org whose grant opened `workspace_id` (#419): a workspace entered as a visitor. */
  acting_org_id?: string | null;
  role: string | null;
  impersonated_by: string | null;
  /**
   * In this context by platform power alone (#702): a platform admin holding no
   * membership here, who reads it as a viewer and may write nothing in it.
   */
  platform_viewer?: boolean;
  /** The partners serving the selected workspace's customer (#1195); empty outside a workspace. */
  partners?: PartnerRef[];
}

/** `_login_body`: one of two shapes, told apart by `mfa_required`. */
export type LoginResponse =
  | { mfa_required: true; mfa_token: string; methods: string[] }
  | { mfa_required: false; user: PublicUser; session: Record<string, unknown> };

/** `POST /auth/session/context`. */
export interface ContextResponse {
  workspace_id: string | null;
  org_id: string | null;
}

/** Exactly one of the two, which is what `select_context` requires (#15 Q3(c)). */
export type ContextChoice = { workspace_id: string } | { org_id: string };

/**
 * Whether a session has said whose runs it is asking for.
 *
 * The distinction the whole of the console's gating rests on: a session with no
 * context is not an empty run list, it is a session that has not said whose runs
 * it wants (#231).
 */
export function hasContext(session: SessionResponse): boolean {
  return session.workspace_id !== null || session.org_id !== null;
}

/**
 * A session on its own membership of the workspace it is reading: not an organisation
 * acting through a grant, not an impersonation, and not a platform admin's read-only view.
 * What the writes a workspace owns itself are offered to, and nobody else.
 */
export const onOwnMembership = (session: SessionResponse): boolean =>
  session.workspace_id !== null &&
  !session.acting_org_id &&
  !session.impersonated_by &&
  !isPlatformViewer(session);

/** A platform admin's read-only view of a context it holds no membership in (#702). */
export const isPlatformViewer = (session: SessionResponse): boolean => session.platform_viewer === true;

/** The context a signed-in session is reading under, for the rail's label. */
export function contextLabel(session: SessionResponse): string {
  if (session.workspace_id !== null) return `Workspace ${session.workspace_id}`;
  if (session.org_id !== null) return `Partner or customer ${session.org_id}`;
  return "No workspace";
}

export const readSession = (api: Api): Promise<SessionResponse> =>
  api.get<SessionResponse>(SESSION_PATH);

export const signIn = (
  api: Api,
  credentials: { email: string; password: string; trust_device?: boolean },
): Promise<LoginResponse> => api.post<LoginResponse>(LOGIN_PATH, credentials);

export const register = (
  api: Api,
  account: {
    email: string;
    password: string;
    display_name?: string | null;
    /** The new-user half of the invite flow: the context and the role come from it. */
    invite_token?: string;
  },
): Promise<LoginResponse> => api.post<LoginResponse>(REGISTER_PATH, account);

/** `202 {"status": "sent"}` whether or not the address exists: the mail carries the token. */
export const requestPasswordReset = (api: Api, email: string): Promise<{ status: string }> =>
  api.post<{ status: string }>(PASSWORD_RESET_PATH, { email });

export const resetPassword = (api: Api, token: string, newPassword: string): Promise<void> =>
  api.post<void>(PASSWORD_RESET_CONFIRM_PATH, { token, new_password: newPassword });

/** A signed-in, unverified user asks for the verification mail again. */
export const requestEmailVerification = (api: Api): Promise<{ status: string }> =>
  api.post<{ status: string }>(EMAIL_VERIFY_PATH);

export const confirmEmailVerification = (api: Api, token: string): Promise<void> =>
  api.post<void>(EMAIL_VERIFY_CONFIRM_PATH, { token });

/** The link signs in: the cookie is set server-side, as `/auth/login` sets it. */
export const confirmMagicLink = (api: Api, token: string): Promise<LoginResponse> =>
  api.post<LoginResponse>(MAGIC_LINK_CONFIRM_PATH, { token });

export const completeMfa = (
  api: Api,
  challenge: { mfa_token: string; method: string; credential: string },
): Promise<LoginResponse> => api.post<LoginResponse>(MFA_COMPLETE_PATH, challenge);

export const selectContext = (api: Api, choice: ContextChoice): Promise<ContextResponse> =>
  api.post<ContextResponse>(CONTEXT_PATH, choice);

export const signOut = (api: Api): Promise<void> => api.post<void>(LOGOUT_PATH);
